import { z } from 'zod';
import type { CommentThreadDto, CommentEventAction } from './comment-types';
import type { DeckOperation } from './deck-operation-types';
import type {
  DocumentLockDto,
  LockResourceRequest,
  PresenceFocus,
  PresenceParticipant,
} from './presence-types';
import type { WorkbookOperation } from './workbook-operation-types';
import {
  AgentRunIdSchema,
  ChatAttachmentIdSchema,
  DocumentIdSchema,
  SessionIdSchema,
} from './entity-ids';

export const ModelRoleSchema = z.enum([
  'agent',
  'utility',
  'transcript_cleanup',
  'review_voice',
]);
export type ModelRole = z.infer<typeof ModelRoleSchema>;

export const AgentRunStepStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed',
]);
export type AgentRunStepStatus = z.infer<typeof AgentRunStepStatusSchema>;

export const AgentRunStepKindSchema = z.enum([
  'outline',
  'slide-build',
  'slide-freeze',
  'chart',
  'read',
  'reasoning',
  'tool',
  // Sandbox environment provisioning notice — persisted so the timeline's
  // "Isolated environment ready" row survives session restore.
  'environment',
  // Something the run produced or consulted that the chat renders as a card
  // or source chip; `detail` carries the JSON-encoded ArtifactRef.
  'artifact',
]);
export type AgentRunStepKind = z.infer<typeof AgentRunStepKindSchema>;

export const ArtifactRefSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.enum(['deck', 'workbook']),
    id: z.string(),
    name: z.string(),
    origin: z.enum(['created', 'opened']),
  }),
  z.object({
    kind: z.literal('file'),
    /** Workspace-relative path under assets/. */
    path: z.string(),
    name: z.string(),
    size: z.number(),
  }),
  z.object({
    kind: z.literal('space_file'),
    id: z.string(),
    name: z.string(),
  }),
  z.object({
    kind: z.literal('web_source'),
    url: z.string(),
    title: z.string(),
  }),
]);
export type ArtifactRef = z.infer<typeof ArtifactRefSchema>;

export function parseArtifactRef(
  detail: string | null | undefined
): ArtifactRef | null {
  if (!detail) return null;
  try {
    const parsed = ArtifactRefSchema.safeParse(JSON.parse(detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const AgentRunStepSchema = z.object({
  id: z.string(),
  sessionId: SessionIdSchema,
  runId: AgentRunIdSchema,
  activityKey: z.string().nullable().optional(),
  kind: AgentRunStepKindSchema,
  // Auxiliary payload; for reasoning steps this is the full reasoning text.
  detail: z.string().nullable().optional(),
  slide: z.number().nullable().optional(),
  messageIndex: z.number(),
  title: z.string(),
  status: AgentRunStepStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentRunStep = z.infer<typeof AgentRunStepSchema>;

export function isTerminalAgentRunStepStatus(status: AgentRunStepStatus) {
  return status === 'completed' || status === 'failed';
}

export function isActiveAgentRunStepStatus(status: AgentRunStepStatus) {
  return !isTerminalAgentRunStepStatus(status);
}

export function isActiveAgentRunStep(step: Pick<AgentRunStep, 'status'>) {
  return isActiveAgentRunStepStatus(step.status);
}

export const AgentRunStatusSchema = z.enum([
  'queued',
  'running',
  'cancel_requested',
  'cancelled',
  'paused',
  'completed',
  'failed',
  'stale',
]);
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

export const AgentTriggerTypeSchema = z.enum(['websocket', 'http', 'system']);
export type AgentTriggerType = z.infer<typeof AgentTriggerTypeSchema>;

export const AgentRunFailureCodeSchema = z.enum([
  'provider_unavailable',
  'rate_limited',
  'context_overflow',
  'invalid_request',
  'model_refused',
  'server_restart',
  'internal',
]);
export type AgentRunFailureCode = z.infer<typeof AgentRunFailureCodeSchema>;

export const AgentRunFailureSchema = z.object({
  code: AgentRunFailureCodeSchema,
  /** One sentence for the chat; never a status code or SDK string. */
  userMessage: z.string(),
  retryable: z.boolean(),
  /** Raw provider/SDK message, collapsed behind "Details" in the UI. */
  detail: z.string().optional(),
});
export type AgentRunFailure = z.infer<typeof AgentRunFailureSchema>;

export const PcAgentStreamEventTypeSchema = z.enum([
  'run.started',
  'session.title.updated',
  'message.delta',
  'message.completed',
  'reasoning.delta',
  'status.updated',
  'environment.provisioning',
  'environment.ready',
  'step.created',
  'step.updated',
  'deck.changed',
  'outline.updated',
  'run.completed',
  'run.cancelled',
  'error',
]);
export type PcAgentStreamEventType = z.infer<
  typeof PcAgentStreamEventTypeSchema
>;

export const PcAgentStreamEventTypes = {
  runStarted: 'run.started',
  sessionTitleUpdated: 'session.title.updated',
  messageDelta: 'message.delta',
  messageCompleted: 'message.completed',
  reasoningDelta: 'reasoning.delta',
  statusUpdated: 'status.updated',
  environmentProvisioning: 'environment.provisioning',
  environmentReady: 'environment.ready',
  stepCreated: 'step.created',
  stepUpdated: 'step.updated',
  deckChanged: 'deck.changed',
  outlineUpdated: 'outline.updated',
  runCompleted: 'run.completed',
  runCancelled: 'run.cancelled',
  error: 'error',
} as const satisfies Record<string, PcAgentStreamEventType>;

export type PcAgentStreamEvent =
  | {
      type: 'run.started';
      sessionId: string;
      runId: string;
    }
  | {
      type: 'session.title.updated';
      sessionId: string;
      runId?: string;
      title: string;
    }
  | {
      type: 'message.delta';
      sessionId: string;
      runId: string;
      content: string;
    }
  | {
      type: 'message.completed';
      sessionId: string;
      runId: string;
      sequence?: number;
      // Wall-clock at emission inside the loop. Load-bearing for restore
      // ordering: the DB write happens asynchronously downstream, while step
      // rows persist synchronously — without this stamp a fast tool step can
      // sort before the prose segment that preceded it.
      timestamp?: number;
      content: string;
    }
  | {
      type: 'reasoning.delta';
      sessionId: string;
      runId: string;
      content: string;
    }
  | {
      type: 'status.updated';
      sessionId: string;
      runId: string;
      message: string;
    }
  | {
      type: 'environment.provisioning';
      sessionId: string;
      runId: string;
      /** 'new' = first environment for this session; 'restart' = recreated after idle teardown. */
      kind: 'new' | 'restart';
    }
  | {
      type: 'environment.ready';
      sessionId: string;
      runId: string;
    }
  | {
      type: 'step.created' | 'step.updated';
      sessionId: string;
      runId: string;
      step: AgentRunStep;
    }
  | {
      type: 'deck.changed';
      sessionId: string;
      runId: string;
      deckId: string;
      slideId?: string;
      action?: string;
    }
  | {
      type: 'outline.updated';
      sessionId: string;
      runId: string;
      deckId: string;
      outline: string;
    }
  | {
      type: 'run.completed';
      sessionId: string;
      runId: string;
    }
  | {
      type: 'run.cancelled';
      sessionId?: string;
      runId: string;
      reason?: string;
    }
  | {
      type: 'error';
      sessionId?: string;
      runId?: string;
      sequence?: number;
      /** Equals `failure.userMessage`. */
      error: string;
      failure: AgentRunFailure;
    };

export const RealtimeSubscriptionSchema = z.object({
  /** Prefix-discriminated deck_/workbook_ ids. */
  documentIds: z.array(DocumentIdSchema).default([]),
  sessionIds: z.array(SessionIdSchema).default([]),
  agentRunIds: z.array(AgentRunIdSchema).default([]),
});
export type RealtimeSubscription = z.infer<typeof RealtimeSubscriptionSchema>;

export interface DeletedDocumentContainer {
  kind: 'deck' | 'space' | 'folder';
  name: string;
}

export const ChatAttachmentRefSchema = z.object({
  id: ChatAttachmentIdSchema,
});
export type ChatAttachmentRef = z.infer<typeof ChatAttachmentRefSchema>;

export type RealtimeClientMessage =
  | {
      type: 'heartbeat';
    }
  | {
      type: 'subscribe';
      /** Prefix-discriminated deck_/workbook_ id. */
      documentId?: string;
      sessionId?: string;
      agentRunId?: string;
      lastSeenSequence?: number;
    }
  | {
      type: 'presence.update';
      /** Prefix-discriminated deck_/workbook_ id. */
      documentId: string;
      focus: PresenceFocus | null;
      idle?: boolean;
    }
  | {
      type: 'lock.acquire';
      requestId: string;
      /** Prefix-discriminated deck_/workbook_ id. */
      documentId: string;
      resources: LockResourceRequest[];
    }
  | {
      type: 'lock.release';
      /** Prefix-discriminated deck_/workbook_ id. */
      documentId: string;
      resourceIds: string[];
    }
  | {
      type: 'agent.message';
      requestId?: string;
      payload: {
        userIntent: string;
        editorContext?: unknown;
        sessionId?: string;
        spaceId?: string;
        /** Prefix-discriminated deck_/workbook_ id. */
        documentId?: string;
        attachments?: ChatAttachmentRef[];
        commentThreadIds?: string[];
        /** Tour: route to the demo-replay engine instead of the live agent. */
        demoReplayId?: 'sheet' | 'deck' | 'comments';
      };
    }
  | {
      type: 'agent.message.edit';
      requestId?: string;
      sessionId: string;
      messageSequence: number;
      payload: {
        userIntent: string;
        editorContext?: unknown;
        spaceId?: string;
        /** Prefix-discriminated deck_/workbook_ id. */
        documentId?: string;
        attachments?: ChatAttachmentRef[];
        commentThreadIds?: string[];
      };
    }
  | {
      type: 'agent.stop';
      requestId?: string;
      agentRunId: string;
    };

export type RealtimeServerMessage =
  | {
      type: 'heartbeat.ack';
    }
  | {
      type: 'subscribed';
      subscriptions: RealtimeSubscription;
    }
  | {
      type: 'agent.accepted';
      requestId?: string;
      runId: string;
      sessionId?: string | null;
      documentId?: string | null;
    }
  | {
      type: 'agent.event';
      sequence?: number;
      event: PcAgentStreamEvent;
    }
  | {
      type: 'deck.operation';
      deckId: string;
      sequence: number;
      operation: DeckOperation;
      headSequence: number;
    }
  | {
      type: 'workbook.operation';
      workbookId: string;
      sequence: number;
      operation: WorkbookOperation;
      headSequence: number;
    }
  | {
      type: 'document.comment';
      documentId: string;
      action: CommentEventAction;
      threadId: string;
      /** Full thread snapshot; absent for thread.deleted. */
      thread?: CommentThreadDto;
      /** Echo suppression: mutating client's self-assigned id. */
      clientId?: string;
    }
  | {
      type: 'document.deleted';
      documentId: string;
      /** Who deleted it, so open editors can say so. */
      actorUserId: string;
      actorName: string;
      /** Set when the document went away with the deck, space or folder holding it. */
      container?: DeletedDocumentContainer;
    }
  | {
      /** The recipient can no longer view this document; the server already
       *  stopped streaming it to them. */
      type: 'document.access_revoked';
      documentId: string;
    }
  | {
      type: 'presence.state';
      documentId: string;
      /** Full roster snapshot — broadcast-only, never persisted. */
      participants: PresenceParticipant[];
    }
  | {
      type: 'document.locks';
      documentId: string;
      /** Full live-lock snapshot for the document. */
      locks: DocumentLockDto[];
    }
  | {
      type: 'lock.result';
      requestId: string;
      documentId: string;
      granted: string[];
      held: DocumentLockDto[];
    }
  | {
      type: 'error';
      error: string;
      requestId?: string;
      /** Present when a request failed before a run started. */
      failure?: AgentRunFailure;
      /** Set when a subscribe was denied for this document's space ACL. */
      deniedDocumentId?: string;
    };
